import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  CheckCircle2,
  CircleDot,
  Clock3,
  Wallet,
  X,
  XCircle,
} from "lucide-react";
import axios from "../../../../axios";
import Loding from "../../../../componant/Loader/Loding";
import { errorToast } from "../../../../utils/toast";
import { useUser } from "../../../../hooks/use-user";
import { getContestReturnUrl } from "./contestNavigation";

const getResultPayload = (response) =>
  response?.data?.result || response?.data?.data || response?.data || {};

const pickArray = (payload) => {
  const candidates = [
    payload?.questions,
    payload?.answerKey,
    payload?.answers,
    payload?.records,
    payload?.items,
    Array.isArray(payload) ? payload : null,
  ];

  return candidates.find((item) => Array.isArray(item)) || [];
};

const normalizeText = (value) =>
  value === undefined || value === null ? "" : String(value).trim();

const normalizeForCompare = (value) => normalizeText(value).toLowerCase();

const formatTime = (seconds) => {
  const value = Number(seconds || 0);
  if (!Number.isFinite(value) || value <= 0) return "0s";

  const mins = Math.floor(value / 60);
  const secs = Math.floor(value % 60);
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
};

const getQuestionSource = (item) => {
  if (item?.question && typeof item.question === "string") {
    return item;
  }

  return (
    item?.question ||
    item?.contestQuestion ||
    item?.contestquestion ||
    item?.questionData ||
    item
  );
};

const getOptions = (source, language) => {
  const options =
    language === "hi"
      ? source?.optionsHin || source?.optionsHindi || source?.hindiOptions
      : source?.options || source?.optionsEng || source?.englishOptions;

  if (Array.isArray(options)) return options;

  return [
    source?.optionA,
    source?.optionB,
    source?.optionC,
    source?.optionD,
  ].filter((option) => option !== undefined && option !== null);
};

const resolveQuestion = (item, language, index) => {
  const source = getQuestionSource(item);
  const question =
    language === "hi"
      ? source?.questionHin ||
        source?.questionHindi ||
        source?.hindiQuestion ||
        source?.question
      : source?.question || source?.questionEng || source?.englishQuestion;

  const correctAnswer =
    language === "hi"
      ? item?.correctAnswerHin ||
        item?.rightAnsHin ||
        source?.answerHin ||
        source?.correctAnswerHin ||
        item?.correctAnswer ||
        source?.answer
      : item?.correctAnswer ||
        item?.rightAnswer ||
        item?.rightAnsEng ||
        source?.answer ||
        source?.correctAnswer;

  const userAnswer =
    language === "hi"
      ? item?.userAnswerHin ||
        item?.selectedAnswerHin ||
        item?.answerHin ||
        item?.userAnswer ||
        item?.selectedAnswer ||
        item?.answer
      : item?.userAnswer ||
        item?.selectedAnswer ||
        item?.submittedAnswer ||
        item?.answer;

  const type = normalizeText(item?.type || item?.status).toLowerCase();
  const isSkipped =
    item?.isSkipped === true ||
    item?.skipped === true ||
    type === "skip" ||
    type === "skipped" ||
    !normalizeText(userAnswer);
  const isCorrect =
    !isSkipped &&
    (item?.ansRight === true ||
      item?.isCorrect === true ||
      normalizeForCompare(userAnswer) === normalizeForCompare(correctAnswer));

  return {
    id: item?._id || source?._id || index,
    number: item?.questionNo || item?.number || index + 1,
    question: normalizeText(question) || `Question ${index + 1}`,
    options: getOptions(source, language),
    correctAnswer: normalizeText(correctAnswer),
    userAnswer: normalizeText(userAnswer),
    timeTaken: item?.timeTaken || item?.secondsTaken || item?.duration || 0,
    isSkipped,
    isCorrect,
  };
};

const AnswerBadge = ({ question }) => {
  if (question.isSkipped) {
    return (
      <span className="inline-flex items-center gap-2 rounded-full bg-[#F4ECFF] px-4 py-2 text-sm font-semibold text-[#6B2B86]">
        <CircleDot size={16} />
        Skipped
      </span>
    );
  }

  if (question.isCorrect) {
    return (
      <span className="inline-flex items-center gap-2 rounded-full bg-[#E7F8EE] px-4 py-2 text-sm font-semibold text-[#15935E]">
        <CheckCircle2 size={16} />
        Correct
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-[#FDE9EC] px-4 py-2 text-sm font-semibold text-[#C83E4D]">
      <XCircle size={16} />
      Wrong
    </span>
  );
};

const ContestAnswerKey = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnUrl = location.state?.returnUrl || getContestReturnUrl();
  const { data: user } = useUser();
  const contestId = location.state?.contestId;
  const [language, setLanguage] = useState("en");
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    const fetchAnswerKey = async () => {
      if (!contestId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.get(`/contest/${contestId}/answer-key`);
        setPayload(getResultPayload(response));
      } catch (error) {
        errorToast(
          error?.response?.data?.message || "Failed to load answer key",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchAnswerKey();
  }, [contestId]);

  const rows = useMemo(
    () =>
      pickArray(payload).map((item, index) =>
        resolveQuestion(item, language, index),
      ),
    [payload, language],
  );

  const contestName =
    payload?.contest?.contestName ||
    payload?.contestName ||
    location.state?.contestName ||
    "Contest";
  const walletBalance =
    user?.unifiedWallet?.balance ??
    user?.unifiedWallet?.contestBalance ??
    user?.walletBalance ??
    0;

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F3ECFA]">
        <Loding />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F3ECFA] pb-28 text-[#201B27]">
      <div className="mx-auto max-w-[720px] px-4 py-5">
        <button
          type="button"
          onClick={() => navigate(returnUrl)}
          className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#562566] shadow-sm"
        >
          <ArrowLeft size={20} />
        </button>

        <div className="flex flex-col-1 items-center justify-between gap-4 rounded-3xl border border-[#E1D5EA] bg-[#F4ECFF] p-5 shadow-sm">
          <div className="min-w-0">
            <h2 className="text-left text-xl font-bold text-[#562566]">
              {contestName}
            </h2>
            <p className="mt-2 text-left leading-4 text-[#5F5968]">
              Review each question with your answer and the correct answer
            </p>
          </div>

          <div className="flex h-20 w-32 shrink-0 flex-col items-center justify-center rounded-2xl bg-[#562566] text-white">
            <span className="text-xl font-bold">{rows.length}</span>
            <span className="mt-2 text-sm">Questions</span>
          </div>
        </div>

        <div className="mt-2 flex justify-end">
          <div className="grid grid-cols-2 rounded-full bg-white p-2 shadow-md">
            <button
              type="button"
              onClick={() => setLanguage("en")}
              className={`rounded-full px-6 py-2 text-md font-semibold ${
                language === "en"
                  ? "bg-[#562566] text-white"
                  : "text-[#6C6172]"
              }`}
            >
              EN
            </button>
            <button
              type="button"
              onClick={() => setLanguage("hi")}
              className={`rounded-full px-6 py-2 text-md font-semibold ${
                language === "hi"
                  ? "bg-[#562566] text-white"
                  : "text-[#6C6172]"
              }`}
            >
              हिंदी
            </button>
          </div>
        </div>

        <div className="mt-4 space-y-6">
          {rows.map((question) => (
            <div
              key={question.id}
              className="rounded-3xl bg-white p-6 shadow-[0_6px_18px_rgba(54,33,69,0.14)]"
            >
              <div className="mb-3 flex items-start justify-between gap-4">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#F4ECFF] text-sm font-bold text-[#562566]">
                  Q{question.number}
                </span>
                <AnswerBadge question={question} />
 
              </div>
              {/* Question */}
  <h2 className="text-left text-[22px] font-medium leading-8 text-[#1F1B24]">
    {question.question}
  </h2>
          

              <div className="mt-5 flex flex-wrap gap-3">
                <span className="inline-flex items-center gap-2 rounded-full bg-[#F8F3FA] px-4 py-2 text-sm font-semibold text-[#6C6172]">
                  <Clock3 size={17} />
                  {formatTime(question.timeTaken)}
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-[#F8F3FA] px-4 py-2 text-sm font-semibold text-[#6C6172]">
                  <CircleDot size={17} />
                  {question.isSkipped ? "Skipped" : "Answered"}
                </span>
              </div>

              <div className="mt-4 space-y-2">
                {question.options.map((option, index) => {
                  const optionText = normalizeText(option);
                  const isCorrect =
                    normalizeForCompare(optionText) ===
                    normalizeForCompare(question.correctAnswer);
                  const isUserAnswer =
                    normalizeForCompare(optionText) ===
                    normalizeForCompare(question.userAnswer);
                  const isWrongUserAnswer =
                    isUserAnswer && !isCorrect && !question.isSkipped;
                  const optionLetter = String.fromCharCode(65 + index);

                  return (
                    <div
                      key={`${question.id}-${optionLetter}`}
                      className={`flex min-h-[72px] items-center gap-4 rounded-2xl border px-3 py-2 text-left ${
                        isCorrect
                          ? "border-[#7DDDB1] bg-[#E9F8EF]"
                          : isWrongUserAnswer
                          ? "border-[#EF9EAA] bg-[#FDE9EC]"
                          : "border-[#DDD6E3] bg-[#FBF8FD]"
                      }`}
                    >
                      <span
                        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full border text-base font-bold ${
                          isCorrect
                            ? "border-[#22B978] bg-[#22B978] text-white"
                            : isWrongUserAnswer
                            ? "border-[#E0475F] bg-[#E0475F] text-white"
                            : "border-[#CFC7D8] bg-white text-[#7B7282]"
                        }`}
                      >
                        {isCorrect ? (
                          <Check size={22} />
                        ) : isWrongUserAnswer ? (
                          <X size={22} />
                        ) : (
                          optionLetter
                        )}
                      </span>

                      <span className="min-w-0 flex-1 text-xl font-medium">
                        {optionText}
                      </span>

                      {isCorrect && (
                        <span className="rounded-full bg-[#D9F3E5] px-4 py-2 text-sm font-semibold text-[#15935E]">
                          Correct
                        </span>
                      )}
                      {isWrongUserAnswer && (
                        <span className="rounded-full bg-[#FAD5DB] px-4 py-2 text-sm font-semibold text-[#C83E4D]">
                          Your Answer
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {!rows.length && (
            <div className="rounded-3xl bg-white px-5 py-12 text-center text-[#6C6172] shadow-sm">
              Answer key is not available yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContestAnswerKey;
